import { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { ShieldAlert, UserCircle } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { QueryContact } from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { LogoAnimation } from "@/components/Logo/logoAnimation";
import { useKeycloak } from "@/providers/KeycloakProvider";

interface ContactGuardProps {
  children: ReactNode;
}

export const ContactGuard = ({ children }: ContactGuardProps) => {
  const { linkedContact, access } = useKeycloak();
  const navigate = useNavigate();
  const { data, loading } = useGetSavedQuery2<QueryContact>(
    "advPortalContactsSimple",
    {
      contactName: "%",
      advisorId:
        access.isAdmin || access.isBackoffice ? "admin" : linkedContact || "",
    }
  );
  const { contactId } = useParams();
  const [countdown, setCountdown] = useState(5);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  // Check access when data is loaded
  useEffect(() => {
    if (!loading && data) {
      const accessGranted = data.some(
        (contact) => contact.id === Number(contactId)
      );
      setHasAccess(accessGranted);
    }
  }, [loading, data, contactId]);

  // Countdown timer
  useEffect(() => {
    if (hasAccess === false) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [hasAccess]);

  // Redirect when countdown reaches 0
  useEffect(() => {
    if (hasAccess === false && countdown <= 0) {
      navigate("/");
    }
  }, [hasAccess, countdown, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "70vh",
          p: 3,
        }}
        className="bg-background"
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            maxWidth: 500,
            width: "100%",
            textAlign: "center",
            borderRadius: 3,
          }}
          className="bg-card border border-border"
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
            className="bg-primary/10"
          >
            <UserCircle size={40} className="text-primary" />
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            className="text-foreground"
          >
            Contact Loading
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 3, lineHeight: 1.6 }}
            className="text-muted-foreground"
          >
            Please wait while we verify your access...
          </Typography>

          <div className="flex justify-center items-center w-full">
            <LogoAnimation size={54} variant="spin" />
          </div>
        </Paper>
      </Box>
    );
  }

  if (hasAccess === false) {
    const progress = ((5 - countdown) / 5) * 100;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "70vh",
          p: 3,
        }}
        className="bg-background"
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            maxWidth: 500,
            width: "100%",
            textAlign: "center",
            borderRadius: 3,
          }}
          className="bg-card border-2 border-destructive/30"
          style={{
            backgroundColor: "hsl(var(--destructive) / 0.08)",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
            style={{
              backgroundColor: "hsl(var(--destructive) / 0.15)",
            }}
          >
            <ShieldAlert size={40} className="text-destructive" />
          </Box>

          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            className="text-destructive"
          >
            Access Denied
          </Typography>

          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.6 }}
            className="text-muted-foreground"
          >
            You do not have access to this contact. You will be automatically
            redirected to the home page.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{ mb: 1 }}
              className="text-destructive"
            >
              {countdown}
            </Typography>
            <Typography variant="body2" className="text-muted-foreground">
              seconds
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "hsl(var(--destructive) / 0.1)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                backgroundColor: "hsl(var(--destructive))",
              },
            }}
          />
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};
